import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { DollarSign, AlertCircle, Link as LinkIcon, CheckCircle, Gift, Sparkles, Building } from 'lucide-react';
import { getInvitation, consumeInvitation } from '../services/userService';

export const UserSetupForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useFirebase();
  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [invitedRole, setInvitedRole] = useState<'admin' | 'manager' | 'employee'>('employee');
  const [invitedUserName, setInvitedUserName] = useState('');
  const [inviteId, setInviteId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteFromUrl = params.get('invite');
    const inviteFromSession = sessionStorage.getItem('pendingInvite');
    const inviteCode = inviteFromUrl || inviteFromSession;

    if (inviteCode) {
      const checkInvite = async () => {
        setStatus('loading');
        try {
          const invite = await getInvitation(inviteCode);
          if (invite && invite.status === 'pending') {
            setTenantId(invite.tenantId);
            setInvitedRole(invite.role);
            setInvitedUserName(invite.userName);
            setInviteId(inviteCode);
            setStatus('idle');
            // Clean up session storage after consuming/detecting
            sessionStorage.removeItem('pendingInvite');
          } else {
            setStatus('error');
            setMessage('This invite link is invalid or has already been used.');
            sessionStorage.removeItem('pendingInvite');
          }
        } catch (error) {
          console.error('Error checking invite:', error);
          setStatus('idle');
        }
      };
      checkInvite();
    }
  }, []);

  const handleSave = async () => {
    if (!user) return;
    if (!tenantId) {
        setStatus('error');
        setMessage('Please enter a valid Tenant ID.');
        return;
    }
    setStatus('loading');
    
    try {
      // Basic check if tenant exists
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
      console.log('DEBUG: Linking attempt to tenant:', tenantId, 'exists:', tenantDoc.exists());
      if (!tenantDoc.exists()) {
        setStatus('error');
        setMessage('That Tenant ID does not exist. Please check and try again.');
        return;
      }

      console.log('DEBUG: Tenant exists, proceeding to link user:', user.uid);
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        tenantId,
        email: user.email || '',
        name: invitedUserName || 'New User',
        role: invitedRole || 'employee'
      });
      console.log('DEBUG: User linked successfully');

      if (inviteId) {
        await consumeInvitation(inviteId);
      }

      setStatus('success');
      setMessage('Account linked successfully! Redirecting...');
      // Small delay to show success before context update triggers re-render/redirect
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error linking account:', error);
      setStatus('error');
      setMessage('An error occurred while linking your account. Please try again.');
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 space-y-6">
        <div className="text-center space-y-2">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto text-white mb-4 ${inviteId ? 'bg-amber-500 animate-bounce shadow-lg shadow-amber-200' : 'bg-blue-600'}`}>
              {inviteId ? <Sparkles size={32} /> : <LinkIcon size={32} />}
            </div>
            <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">
              {inviteId ? 'Welcome to the Team!' : 'Link Your Account'}
            </h1>
            <p className="text-sm text-gray-500 font-medium">Logged in as {user?.email}</p>
        </div>

        <div className="space-y-4">
          {inviteId ? (
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 flex items-start gap-3">
              <Gift size={20} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-xs font-bold text-amber-900">Invite Detected!</p>
                <p className="text-[11px] text-amber-700 leading-tight">
                  You've been invited as <strong>{invitedUserName}</strong> to join <strong>{tenantId}</strong> with <strong>{invitedRole}</strong> permissions. Just click the button below to complete setup.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 leading-relaxed">
                To proceed, please enter your business's <strong>Tenant ID</strong>. This links your account to your specific TipValue AI instance.
              </p>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Don't have this?</p>
              <p className="text-xs text-gray-500">Contact your manager if you're unsure of your Tenant ID.</p>
            </>
          )}
        </div>

        {!inviteId && (
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Tenant ID</label>
            <input 
              value={tenantId} 
              onChange={e => setTenantId(e.target.value)} 
              placeholder="e.g., demo-tenant"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition"
            />
          </div>
        )}

        <button 
          onClick={handleSave}
          disabled={status === 'loading'}
          className={`w-full text-white rounded-xl py-4 font-black uppercase tracking-widest transition-all active:scale-95 disabled:bg-gray-300 ${
            inviteId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-900 hover:bg-blue-600'
          }`}
        >
          {status === 'loading' ? 'Linking...' : inviteId ? 'Join Organization' : 'Link Business Instance'}
        </button>

        {!inviteId && (
          <div className="pt-1 text-center">
            <button
              type="button"
              onClick={() => navigate('/onboarding')}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors"
            >
              <Building size={14} />
              <span>Setting up a new business instead? Create a Business Profile &rarr;</span>
            </button>
          </div>
        )}

        <button 
          onClick={() => signOut(auth)} 
          className="w-full text-center text-gray-400 text-xs font-bold uppercase tracking-widest mt-3 hover:text-gray-900 transition-colors"
        >
          Not your account? Sign Out
        </button>

        {status === 'success' && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3 text-emerald-700">
            <CheckCircle size={20} />
            <p className="text-xs font-bold">{message}</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-700">
            <AlertCircle size={20} />
            <p className="text-xs font-bold">{message}</p>
          </div>
        )}
      </div>
    </main>
  );
};
